/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailNotifications.js
 * @description
 * Local-notification fallback + deep-link responder for the in-app mail client,
 * so new-mail alerts behave like a normal mobile mail app EVEN when server-side
 * FCM push is unavailable (e.g. while the Firebase credentials are being fixed).
 *
 * Strategy:
 *  - A periodic `expo-background-fetch` task polls `/api/mail/notifications/poll`
 *    (the same endpoint the web client uses) for mail notifications created since
 *    the last seen timestamp, and raises a LOCAL `expo-notifications` banner for
 *    each new one — deep-linking to the relevant thread on tap.
 *  - When real FCM push IS delivered, the existing `PushNotificationsBootstrap`
 *    path handles it; this is a strict, idempotent fallback (the `since` cursor
 *    prevents duplicates), not a replacement.
 *  - A response listener routes BOTH local and (background) push taps through the
 *    shared {@link resolveNotificationNavigationTarget} resolver to MailThreadScreen.
 *
 * `TaskManager.defineTask` MUST run at module load (before registration), so this
 * module is imported eagerly from App.jsx.
 */

import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import {getAccessToken} from './storage';
import {markNotificationLaunchIntent} from './notificationLaunchIntent';
import {navigateWhenReady} from './navigationRef';
import {resolveNotificationNavigationTarget} from './notificationNavigation';
import {isWorkdayReminderPayload, requestWorkdayReminder} from './workdayBus';
import {isDriverTarget, openDriverTarget} from './driverPushRouting';
import {ApiConfig} from '../config/ApiConfig';
import {refreshAppBadge} from './appBadge';
import {loadStoredPushNotificationState, savePendingOpenNotification} from '../services/pushNotifications';
import {
    getMailMessage,
    moveMailMessage,
    queueMailMessage,
    updateMailMessageFlags,
} from '../services/mailApi';
import {
    patchCachedMailMessage,
    prefetchMailMessage,
    prefetchMailThread,
    primeMailStubFromPush,
    toPlainMailPreview,
} from './mailCache';
import {
    ensureNotificationChannels,
    resolveChannelIdForPushData,
    resolveThreadIdentifierForPushData,
} from './notificationChannels';
import i18n from '../../i18n';
import {isPushForActiveChatView} from './teamChat/activeChatView';

export const FETCH_MAIL_TASK = 'FETCH_MAIL_TASK';
const LAST_SEEN_KEY = '@mail_last_notification_at';
const MAIL_CHANNEL_ID = 'mail';
// Derived from the single API base so the poll host can never drift from the
// rest of the app (mailApi.js uses the same ApiConfig.apiURL).
const POLL_URL = `${ApiConfig.apiURL}/api/mail/notifications/poll`;
// Identifier of the last cold-start notification response we acted on, so
// `getLastNotificationResponseAsync()` (which keeps returning the SAME response
// on later launches) can never hijack a normal, user-initiated app start.
const LAST_HANDLED_RESPONSE_KEY = '@mail_last_handled_response';
// A cold-start response older than this is treated as "the user already dealt
// with it" and ignored.
const COLD_START_RESPONSE_MAX_AGE_MS = 5 * 60 * 1000;
// The navigator can be absent for a long time on a cold, push-driven launch
// (runtime-state load ≤ 8 s + splash ≈ 1.5-5 s), so a mail tap is retried for a
// bounded window instead of being dropped — mirroring the repo's existing
// deep-link retry (helpers/deepLink/adminLinkNavigation.js, widgets/widgetDeepLink.js).
const NAV_RETRY_ATTEMPTS = 60;
const NAV_RETRY_MS = 250;

/**
 * Notification category every mail banner is tagged with. The backend attaches
 * the SAME value as `aps.category` on its `type:'mail'` remote pushes, so iOS
 * remote alerts surface the identical action set.
 * @type {string}
 */
export const MAIL_NOTIFICATION_CATEGORY = 'mail_message';

/**
 * Action identifiers of the {@link MAIL_NOTIFICATION_CATEGORY} category.
 * `reply`/`markRead`/`archive` run headlessly (`opensAppToForeground: false`);
 * `open` foregrounds the app and rides the normal tap navigation path.
 * @type {{reply: string, markRead: string, archive: string, open: string}}
 */
export const MAIL_NOTIFICATION_ACTIONS = {
    reply: 'reply',
    markRead: 'mark_read',
    archive: 'archive',
    open: 'open',
};

// Persisted queue of mail actions whose API call failed (offline, token refresh
// mid-flight, …) — replayed on the next boot/foreground, newest first.
const PENDING_MAIL_ACTIONS_KEY = '@mail_pending_notification_actions';
const PENDING_MAIL_ACTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PENDING_MAIL_ACTION_MAX_ITEMS = 10;
const PENDING_MAIL_ACTION_MAX_ATTEMPTS = 3;
// Stable identifier of the Android "N new messages" group-summary banner, so a
// later poll batch UPDATES it in place instead of stacking summaries.
const MAIL_SUMMARY_NOTIFICATION_ID = 'mail:summary';

// Show mail notifications as banners even while the app is foregrounded, with
// sound + badge — matching native mail-client behaviour. SDK 53 fields.
// Exception: a Team Chat message of the conversation already open on screen is
// swallowed silently (it is visible in the list the moment it arrives).
Notifications.setNotificationHandler({
    handleNotification: async (notification) => {
        const content = notification?.request?.content || {};
        const payload = notification?.request?.trigger?.payload || {};
        const data = {...(payload && typeof payload === 'object' ? payload : {}), ...(content.data || {})};
        const silent = isPushForActiveChatView(data);

        return {
            shouldShowBanner: !silent,
            shouldShowList: !silent,
            shouldPlaySound: !silent,
            shouldSetBadge: true,
        };
    },
});

/**
 * Ensure the dedicated mail notification channel exists (now part of the shared
 * localized per-category channel set). No-op on iOS / web.
 * @returns {Promise<void>}
 */
const ensureMailChannel = async () => {
    await ensureNotificationChannels();
};

/**
 * Ensure every Android notification channel REMOTE FCM pushes can target exists
 * (localized per-category channels + the legacy 'default' channel — see
 * notificationChannels.js). Android 8+ silently DROPS a notification whose
 * channel id does not exist, so channels must be created BEFORE any push
 * arrives.
 *
 * Channels are app-global in Android, so a channel created here via the native
 * NotificationManager is also resolved by the Firebase Messaging SDK when it
 * builds the system-tray notification in the background/killed state.
 *
 * @returns {Promise<void>}
 */
export const ensureDefaultPushChannel = async () => {
    await ensureNotificationChannels();
};

/**
 * Register (or refresh) the actionable 'mail_message' notification category on
 * BOTH platforms: an inline text-input Reply, headless Mark-as-read and Archive
 * (none of the three foregrounds the app) plus an explicit Open action that
 * behaves like the default tap. Action titles are localized with the CURRENT
 * app language; the module re-runs this on every i18n language change (cheap —
 * a single native upsert by identifier).
 *
 * Every locally presented mail banner carries this `categoryIdentifier`, and the
 * backend attaches the same value as `aps.category` to its remote mail pushes so
 * iOS remote alerts surface the identical actions.
 *
 * @returns {Promise<boolean>} true when the category was (re)registered.
 */
export const registerMailNotificationCategory = async () => {
    if (Platform.OS === 'web') {
        return false;
    }
    if (!Notifications || typeof Notifications.setNotificationCategoryAsync !== 'function') {
        return false;
    }

    try {
        await Notifications.setNotificationCategoryAsync(MAIL_NOTIFICATION_CATEGORY, [
            {
                identifier: MAIL_NOTIFICATION_ACTIONS.reply,
                buttonTitle: i18n.t('mail_reply', {defaultValue: 'Válasz'}),
                textInput: {
                    submitButtonTitle: i18n.t('mail_compose_send', {defaultValue: 'Küldés'}),
                    placeholder: i18n.t('mail_action_reply_placeholder', {defaultValue: 'Válasz írása…'}),
                },
                options: {opensAppToForeground: false},
            },
            {
                identifier: MAIL_NOTIFICATION_ACTIONS.markRead,
                buttonTitle: i18n.t('mail_action_mark_read', {defaultValue: 'Megjelölés olvasottként'}),
                options: {opensAppToForeground: false},
            },
            {
                identifier: MAIL_NOTIFICATION_ACTIONS.archive,
                buttonTitle: i18n.t('mail_archive', {defaultValue: 'Archiválás'}),
                options: {opensAppToForeground: false},
            },
            {
                identifier: MAIL_NOTIFICATION_ACTIONS.open,
                buttonTitle: i18n.t('mail_action_open', {defaultValue: 'Megnyitás'}),
                options: {opensAppToForeground: true},
            },
        ]);

        return true;
    } catch (_e) {
        // Category registration is an enhancement — a failure must never block boot.
        return false;
    }
};

// Keep the action titles in the user's app language (mirrors the localized
// channel names in notificationChannels.js). Registration is a single upsert
// by identifier, so re-running per language switch is cheap.
try {
    i18n.on('languageChanged', () => {
        registerMailNotificationCategory().catch(() => {});
    });
} catch (_e) {
    // i18n emitter unavailable (tests) — the boot-time registration still runs.
}

/**
 * Whether real server-side FCM push is active on this device. When it is, the
 * backend already delivers a `type:'mail'` push for every incoming message (via
 * the Notification post-save hook), so this local poll must NOT raise its own
 * banner — otherwise every email double-notifies (one FCM banner on the
 * 'default' channel + one local banner on the 'mail' channel). The poll then
 * only advances its cursor so it can seamlessly take over if FCM ever stops.
 *
 * @returns {Promise<boolean>}
 */
const isFcmPushActive = async () => {
    try {
        const state = await loadStoredPushNotificationState();
        return state?.isEnabled === true && state?.tokenRegistered === true;
    } catch (_e) {
        // If we cannot tell, assume FCM is NOT active so the fallback still fires.
        return false;
    }
};

/** Mail event keys that report on a message the user sent, not on an incoming one. */
const MAIL_SENT_NOTICE_EVENTS = new Set(['mail.opened', 'mail.send_failed']);

/**
 * Mail push events that are NOT an incoming message: sent-mail notices plus the private thread
 * discussion (comment / @mention - inline Reply / Archive would act on the wrong thing and the
 * banner must never replace the conversation's incoming-mail banner). Follow-up reminders
 * (`mail.snooze_due`) stay actionable: replying is exactly what they ask for.
 */
const MAIL_NON_ACTIONABLE_EVENTS = new Set([...MAIL_SENT_NOTICE_EVENTS, 'mail.thread_comment', 'mail.thread_mention']);

/**
 * Whether a notification payload belongs to the mail client.
 * @param {Object} [data] Notification `data` payload.
 * @returns {boolean}
 */
const isMailPayload = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const type = String(data.type || data.eventKey || '').toLowerCase();

    return type === 'mail'
        || type.startsWith('mail.')
        || Boolean(data.mailMessageId || data.mailmessageid || data.threadKey || data.threadkey);
};

/**
 * Android tray identifier for a locally presented mail banner, keyed on the
 * conversation. Re-using the identifier makes a newer message of the SAME
 * conversation update the existing banner in place, so the tray keeps one entry
 * per conversation — the closest expo-notifications gets to native `setGroup`
 * conversation grouping (its JS API exposes no group/summary fields). Falls
 * back to the account when the payload carries no threadKey; returns '' (= let
 * the OS pick a random id) when neither is present.
 *
 * @param {Object} [data] Notification `data` payload.
 * @returns {string} Stable per-conversation identifier, or ''.
 */
const resolveMailCollapseIdentifier = (data) => {
    const threadKey = String(data?.threadKey || data?.threadkey || '').trim();
    if (threadKey) {
        return `mail:${threadKey}`;
    }
    const accountId = String(data?.accountId || data?.accountid
        || data?.mailboxAccountId || data?.mailboxaccountid || '').trim();
    if (accountId) {
        return `mail:account:${accountId}`;
    }

    return '';
};

/**
 * React to a mail notification the MOMENT it is observed — on receipt as well as
 * on tap. Two things happen, both fire-and-forget:
 *   1. a placeholder message is cached from the payload so the reader can paint
 *      sender/subject/preview on its very first frame, and
 *   2. the real message (and, when known, its conversation) is prefetched, so by
 *      the time the user taps, the body is usually already in memory.
 *
 * Never awaited by callers and never throws: a failed prefetch simply falls back
 * to the reader's own fetch.
 *
 * @param {Object} [data] Notification `data` payload (FCM or local).
 * @returns {boolean} true when the payload was a mail payload we acted on.
 */
export const observeMailPushPayload = (data) => {
    try {
        if (!isMailPayload(data)) {
            return false;
        }
        primeMailStubFromPush(data);

        const messageId = String(data.mailMessageId || data.mailmessageid || '').trim();
        const threadKey = String(data.threadKey || data.threadkey || '').trim();
        if (messageId) {
            // Single message first: it is the cheap request and the one the
            // reader paints from. The thread only fills in the rest.
            prefetchMailMessage(messageId);
        }
        if (threadKey) {
            prefetchMailThread(threadKey);
        }

        return Boolean(messageId || threadKey);
    } catch (_e) {
        return false;
    }
};

/**
 * Navigate to a resolved notification target as soon as the navigation container
 * exists, instead of dropping the intent when it is not mounted yet.
 *
 * Retries `navigateWhenReady` for a bounded window and, if the navigator STILL
 * is not up (pathological boot), hands the intent to the app's existing
 * persisted queue (`savePendingOpenNotification`) which `PushNotificationsBootstrap`
 * replays via `consumePendingOpenNotification()`. No second queue is introduced.
 *
 * @param {{name?: string, params?: Object}} target Resolved navigation target.
 * @param {{data?: Object, notification?: Object}} [message] Original message, persisted as the fallback.
 * @returns {void}
 */
export const navigateToNotificationTarget = (target, message) => {
    if (!target?.name) {
        return;
    }

    let attempts = 0;
    const tryNavigate = () => {
        attempts += 1;
        let done = false;
        try {
            done = navigateWhenReady(target.name, target.params || {});
        } catch (_e) {
            done = false;
        }
        if (done) {
            return;
        }
        if (attempts < NAV_RETRY_ATTEMPTS) {
            globalThis.setTimeout(tryNavigate, NAV_RETRY_MS);

            return;
        }
        // Last resort: persist so the push bootstrap replays it on mount.
        if (message) {
            savePendingOpenNotification(message).catch(() => {});
        }
    };

    tryNavigate();
};

/**
 * Present an incoming FCM push as a LOCAL notification so it is visible, sounds,
 * lands in the tray and — crucially — is TAPPABLE (routing through the shared
 * responder). Used for:
 *   - FOREGROUND pushes: the OS never shows the FCM `notification` block while
 *     the app is foregrounded, so without this a push is silent (or, previously,
 *     only a non-tappable in-app toast).
 *   - DATA-ONLY pushes in the background (safety net): a push with no FCM
 *     `notification` block would otherwise never render a system banner.
 *
 * The FCM `data` (clickAction/screen/mailMessageId/threadKey/eventKey/…) is
 * copied verbatim into the local notification so a tap deep-links exactly like a
 * real push tap via {@link setupMailNotificationResponder}.
 *
 * @param {{notification?:{title?:string, body?:string}, data?:Object}} message normalized remote message
 * @returns {Promise<boolean>} true when a local banner was scheduled.
 */
export const presentPushAsLocalNotification = async (message) => {
    if (Platform.OS === 'web' || !message) {
        return false;
    }
    if (!Notifications || typeof Notifications.scheduleNotificationAsync !== 'function') {
        return false;
    }

    // Respect an explicit user opt-out: never surface a local banner when the
    // user has turned notifications OFF in settings. (Belt-and-suspenders — the
    // device token is normally removed on disable, but a push already in flight
    // could otherwise still slip through.) `null`/unknown falls through.
    try {
        const pushState = await loadStoredPushNotificationState();
        if (pushState?.isEnabled === false) {
            return false;
        }
    } catch (_e) { /* if we cannot tell, present anyway */ }

    const data = message.data || {};
    const isMail = isMailPayload(data);
    // Notices about a message the user SENT (read receipt, send failure) get no inline
    // reply/mark-read/archive actions and never replace a conversation's incoming banner.
    const isSentMailNotice = MAIL_NON_ACTIONABLE_EVENTS.has(String(data.eventKey || '').toLowerCase());
    const isActionableMail = isMail && !isSentMailNotice;
    // Prefetch on RECEIPT, not just on tap: by the time the banner is tapped the
    // body is usually already cached, so the reader opens instantly.
    observeMailPushPayload(data);
    const title = message.notification?.title
        || data.title
        || i18n.t('notif_foreground_default_title', {defaultValue: 'Új értesítés'});
    const body = message.notification?.body
        || data.body
        || i18n.t('notif_foreground_default_body', {defaultValue: 'Nyisd meg az appot a részletekért.'});

    try {
        await ensureNotificationChannels();
        // Android: one tray entry per conversation (identifier re-use updates it
        // in place). LOCALLY presented banners only — remote hybrid pushes the
        // system tray renders itself are untouched.
        const collapseId = isActionableMail && Platform.OS === 'android' ? resolveMailCollapseIdentifier(data) : '';
        await Notifications.scheduleNotificationAsync({
            ...(collapseId ? {identifier: collapseId} : {}),
            content: {
                title,
                body,
                sound: 'default',
                // Actionable mail banners: inline reply / mark read / archive / open.
                ...(isActionableMail ? {categoryIdentifier: MAIL_NOTIFICATION_CATEGORY} : {}),
                // iOS: break through Focus / silent modes for these personal alerts
                // (best-effort — honoured when the Time Sensitive entitlement is present).
                ...(Platform.OS === 'ios' ? {
                    interruptionLevel: 'timeSensitive',
                    // Bundle in the tray per category (mail: per conversation thread),
                    // mirroring the backend's apns thread-id.
                    threadIdentifier: resolveThreadIdentifierForPushData(data),
                } : {}),
                // Everything the tap responder needs to deep-link, coerced to strings.
                data: Object.keys(data).reduce((acc, key) => {
                    const value = data[key];
                    acc[key] = value == null ? '' : String(value);
                    return acc;
                }, {}),
                // Land on the same per-category channel the backend targets.
                ...(Platform.OS === 'android' ? {channelId: resolveChannelIdForPushData(data)} : {}),
            },
            trigger: null, // deliver immediately
        });
        return true;
    } catch (_e) {
        // Best-effort: never let a presentation failure bubble into the FCM handler.
        return false;
    }
};

/**
 * Poll the backend for new mail notifications and raise a local banner per new
 * item. Uses a persisted `since` cursor for idempotency. On the very first run
 * it only seeds the cursor (so the user is never flooded with a backlog).
 *
 * When FCM push is active this is a NO-OP for banners (see {@link isFcmPushActive}) —
 * it just advances the cursor so a later FCM outage doesn't replay a backlog.
 *
 * @returns {Promise<boolean>} true when at least one new notification was raised.
 */
const pollAndNotify = async () => {
    const token = await getAccessToken();
    if (!token) {
        return false;
    }

    const since = await AsyncStorage.getItem(LAST_SEEN_KEY);
    const url = since ? `${POLL_URL}?since=${encodeURIComponent(since)}` : POLL_URL;

    const response = await fetch(url, {
        method: 'GET',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
    });
    if (!response.ok) {
        return false;
    }

    const data = await response.json().catch(() => null);
    const items = Array.isArray(data?.notifications)
        ? data.notifications
        : (Array.isArray(data) ? data : []);

    // Newest timestamp across the batch becomes the next cursor.
    const newestAt = items.reduce((acc, n) => {
        const ts = n?.createdAt || n?.receivedAt || '';
        return ts > acc ? ts : acc;
    }, since || '');

    // First run (no cursor yet) → just seed, don't notify a backlog.
    if (!since) {
        await AsyncStorage.setItem(LAST_SEEN_KEY, newestAt || new Date().toISOString());
        return false;
    }

    if (!items.length) {
        return false;
    }

    // Respect an explicit opt-out: when the user turned notifications OFF in
    // settings the local mail-poll fallback must stay silent too (just advance
    // the cursor). Without this, disabling push would not silence these banners.
    try {
        const pushState = await loadStoredPushNotificationState();
        if (pushState?.isEnabled === false) {
            if (newestAt) {
                await AsyncStorage.setItem(LAST_SEEN_KEY, newestAt);
            }
            return false;
        }
    } catch (_e) { /* unknown → fall through to the FCM-active check below */ }

    // FCM is delivering these same notifications natively → advance the cursor
    // and bail before raising a duplicate local banner.
    if (await isFcmPushActive()) {
        if (newestAt) {
            await AsyncStorage.setItem(LAST_SEEN_KEY, newestAt);
        }
        return false;
    }

    await ensureMailChannel();

    // Cap the burst so a large sync never spams dozens of banners.
    let presentedCount = 0;
    for (const n of items.slice(0, 5)) {
        const meta = n?.metadata || {};
        // Gmail / Apple Mail convention: the SENDER is the title and the SUBJECT
        // is the body, with the plain-text preview carried in `data` for the
        // reader's instant first paint (never rendered into the banner text,
        // which is what used to leak image tags / markdown junk).
        const senderName = String(meta.fromName || meta.from || '').trim();
        const subject = String(meta.subject || n?.title || '').trim();
        // Sanitized client-side too: a legacy `meta.preview` can still contain
        // HTML/markdown, which must never reach the banner or the reader.
        const preview = toPlainMailPreview(meta.preview);
        const title = senderName || n?.title || i18n.t('mail_title');
        const body = subject || n?.message || preview || '';
        // Prefetch on RECEIPT so the mail is already cached when the user taps.
        observeMailPushPayload({
            type: 'mail',
            mailMessageId: String(meta.mailMessageId || ''),
            threadKey: String(meta.threadKey || ''),
            subject,
            fromName: senderName,
            fromEmail: String(meta.fromEmail || ''),
            preview,
            receivedAt: String(n?.createdAt || ''),
        });
        try {
            // One tray entry per conversation on Android — a newer message of
            // the same thread updates the banner instead of stacking a duplicate
            // (see resolveMailCollapseIdentifier).
            const collapseId = Platform.OS === 'android'
                ? resolveMailCollapseIdentifier({
                    threadKey: String(meta.threadKey || ''),
                    accountId: String(meta.accountId || ''),
                })
                : '';
            await Notifications.scheduleNotificationAsync({
                ...(collapseId ? {identifier: collapseId} : {}),
                content: {
                    title,
                    body,
                    sound: 'default',
                    // Actionable mail banner: inline reply / mark read / archive / open.
                    categoryIdentifier: MAIL_NOTIFICATION_CATEGORY,
                    // iOS: bundle mail banners per conversation thread in the tray.
                    ...(Platform.OS === 'ios' ? {
                        threadIdentifier: meta.threadKey ? `mail:${meta.threadKey}` : 'mail',
                    } : {}),
                    data: {
                        type: 'mail',
                        screen: 'MailThreadScreen',
                        mailMessageId: String(meta.mailMessageId || ''),
                        threadKey: String(meta.threadKey || ''),
                        clickAction: meta.clickAction || (meta.mailMessageId ? `/mail/${meta.mailMessageId}` : ''),
                        // Hints for the reader's instant first paint (see
                        // notificationNavigation.buildMailHintParams). Strings only —
                        // expo-notifications data must stay JSON-primitive.
                        subject,
                        fromName: senderName,
                        fromEmail: String(meta.fromEmail || ''),
                        preview,
                        receivedAt: String(n?.createdAt || ''),
                    },
                    ...(Platform.OS === 'android' ? {channelId: MAIL_CHANNEL_ID} : {}),
                },
                trigger: null, // deliver immediately
            });
            presentedCount += 1;
        } catch (_e) {
            // Never let one bad notification abort the batch.
        }
    }

    // Android group-summary: one quiet, stable banner totalling the batch — the
    // grouped-mail-client convention. expo-notifications exposes no native
    // summary flag, so this is an ordinary banner with a FIXED identifier that
    // later batches update in place; its tap opens the mailbox list.
    if (Platform.OS === 'android' && presentedCount > 1) {
        try {
            await Notifications.scheduleNotificationAsync({
                identifier: MAIL_SUMMARY_NOTIFICATION_ID,
                content: {
                    title: i18n.t('mail_title'),
                    body: i18n.t('mail_new_messages_count', {
                        count: presentedCount,
                        defaultValue: `${presentedCount} új üzenet`,
                    }),
                    data: {type: 'mail', screen: 'MailHomeScreen'},
                    channelId: MAIL_CHANNEL_ID,
                },
                trigger: null,
            });
        } catch (_e) {
            // The summary is decorative — never let it fail the poll.
        }
    }

    if (newestAt) {
        await AsyncStorage.setItem(LAST_SEEN_KEY, newestAt);
    }
    return true;
};

// Background task definition (must be registered at module evaluation time).
TaskManager.defineTask(FETCH_MAIL_TASK, async () => {
    try {
        const raised = await pollAndNotify();
        return raised
            ? BackgroundFetch.BackgroundFetchResult.NewData
            : BackgroundFetch.BackgroundFetchResult.NoData;
    } catch (_e) {
        return BackgroundFetch.BackgroundFetchResult.Failed;
    }
});

/**
 * Register the periodic mail-poll background task (~every 5 minutes) + channel.
 * Safe to call repeatedly; no-op on web.
 * @returns {Promise<boolean>}
 */
export const registerMailNotifications = async () => {
    if (Platform.OS === 'web') {
        return false;
    }
    await ensureMailChannel();
    try {
        await BackgroundFetch.registerTaskAsync(FETCH_MAIL_TASK, {
            minimumInterval: 5 * 60, // 5 minutes (OS may extend this)
            stopOnTerminate: false,
            startOnBoot: true,
        });
        return true;
    } catch (_e) {
        return false;
    }
};

/**
 * Foreground-triggerable single poll. Call e.g. when the mailbox screen gains
 * focus to surface a banner for mail that arrived while the app was idle.
 * @returns {Promise<boolean>}
 */
export const pollMailNotificationsNow = async () => {
    try {
        return await pollAndNotify();
    } catch (_e) {
        return false;
    }
};

/* ──────────── background notification actions (reply / read / archive) ──────────── */

/**
 * Whether an expo-notifications response identifier is one of the HEADLESS mail
 * actions (inline reply / mark read / archive) — i.e. everything except the
 * default tap and the explicit `open` action, which both navigate.
 *
 * @param {string} actionId `response.actionIdentifier`.
 * @returns {boolean}
 */
const isBackgroundMailAction = (actionId) => actionId === MAIL_NOTIFICATION_ACTIONS.reply
    || actionId === MAIL_NOTIFICATION_ACTIONS.markRead
    || actionId === MAIL_NOTIFICATION_ACTIONS.archive;

/**
 * Minimal HTML-escape for the quick-reply body (the only user input that ever
 * becomes HTML here). Newlines become `<br/>` so the plain text keeps its shape.
 *
 * @param {string} text Raw inline-reply text.
 * @returns {string} Escaped HTML fragment.
 */
const escapeQuickReplyHtml = (text) => String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br/>');

/**
 * Send an inline notification reply through the SAME queue endpoint the compose
 * screen uses. The full message is fetched first (mailApi reads the Bearer
 * token straight from storage, so this works headlessly) because the push
 * payload does not carry the RFC message id, the reply-to address or the
 * mailbox account — all three are needed for correct threading on the
 * receiving side.
 *
 * @param {string} messageId DB id of the message being answered.
 * @param {Object} data Notification `data` payload (fallback recipient/subject).
 * @param {string} text Inline reply text as typed by the user.
 * @returns {Promise<void>} Resolves when queued; rejects so the caller can retry.
 */
const sendQuickReplyFromNotification = async (messageId, data, text) => {
    const fetched = await getMailMessage(messageId);
    const message = fetched?.message || fetched;
    if (!message || typeof message !== 'object') {
        throw new Error('Quick reply source message unavailable');
    }

    const sourceAddresses = Array.isArray(message.replyTo) && message.replyTo.length
        ? message.replyTo
        : (Array.isArray(message.from) ? message.from : []);
    const to = sourceAddresses
        .map((address) => ({
            ...(address?.name ? {name: String(address.name)} : {}),
            email: String(address?.email || '').trim(),
        }))
        .filter((address) => address.email);
    if (!to.length) {
        const fallbackEmail = String(data?.fromEmail || data?.fromemail || '').trim();
        if (fallbackEmail) {
            to.push({email: fallbackEmail});
        }
    }
    if (!to.length) {
        throw new Error('Quick reply has no recipient');
    }

    const baseSubject = String(message.subject || data?.subject || '').trim();
    const subject = /^re:/i.test(baseSubject) ? baseSubject : `Re: ${baseSubject}`.trim();
    const html = `<p>${escapeQuickReplyHtml(text)}</p>`;
    const rfcMessageId = String(message.messageId || '').trim();
    const threadKey = String(message.threadKey || data?.threadKey || data?.threadkey || '').trim();

    await queueMailMessage({
        ...(message.mailboxAccountId ? {mailboxAccountId: String(message.mailboxAccountId)} : {}),
        to,
        subject,
        // Both spellings on purpose: mail.service#queueOutgoingMessage reads
        // htmlBody/textBody while the compose screen sends bodyHtml/bodyText —
        // carrying both keeps this correct across backend versions.
        bodyText: text,
        bodyHtml: html,
        textBody: text,
        htmlBody: html,
        ...(rfcMessageId ? {inReplyTo: rfcMessageId, references: rfcMessageId} : {}),
        ...(threadKey ? {threadKey} : {}),
    });
};

/**
 * Execute one mail notification action against the API (used both for the live
 * response and for queued retries). Mutations also patch the in-memory mail
 * cache so an already-prefetched copy reflects the new state instantly.
 *
 * @param {string} actionId One of {@link MAIL_NOTIFICATION_ACTIONS}.
 * @param {string} messageId Target message DB id.
 * @param {string} text Inline reply text ('' for non-reply actions).
 * @param {Object} [data] Original push `data` payload (reply fallbacks).
 * @returns {Promise<boolean>} true when the action was recognized and performed.
 */
const performMailNotificationAction = async (actionId, messageId, text, data) => {
    if (actionId === MAIL_NOTIFICATION_ACTIONS.markRead) {
        await updateMailMessageFlags(messageId, {isRead: true});
        patchCachedMailMessage(messageId, {isRead: true});

        return true;
    }
    if (actionId === MAIL_NOTIFICATION_ACTIONS.archive) {
        // 'Archive' is the mailbox system folder name (MailContext#archiveMessages).
        await moveMailMessage(messageId, {folder: 'Archive'});
        patchCachedMailMessage(messageId, {folder: 'Archive'});

        return true;
    }
    if (actionId === MAIL_NOTIFICATION_ACTIONS.reply) {
        await sendQuickReplyFromNotification(messageId, data, text);

        return true;
    }

    return false;
};

/**
 * Refresh the app icon badge after a mail action, so a mark-as-read performed from
 * the notification shade is reflected on the icon without opening the app.
 *
 * Kept as the mail-side entry point, but the badge itself is owned by `helpers/appBadge`
 * and counts unread notifications AND unread mail — a mail-only badge under-reported
 * everything else the user was waiting on. Best-effort and never throws.
 *
 * @returns {Promise<boolean>} true when the badge was updated.
 */
export const refreshMailBadgeCount = async () => {
    const applied = await refreshAppBadge({force: true});

    return applied !== null;
};

/**
 * Persist a failed mail action for a later retry (bounded list, newest kept).
 * Swallows every storage error — a lost retry is acceptable, a crash is not.
 *
 * @param {{actionId: string, messageId: string, text: string, data: Object, attempts?: number}} entry
 * @returns {Promise<void>}
 */
const queuePendingMailAction = async (entry) => {
    try {
        const raw = await AsyncStorage.getItem(PENDING_MAIL_ACTIONS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        const list = Array.isArray(parsed) ? parsed : [];
        list.unshift({...entry, at: Date.now(), attempts: Number(entry.attempts || 0)});
        await AsyncStorage.setItem(
            PENDING_MAIL_ACTIONS_KEY,
            JSON.stringify(list.slice(0, PENDING_MAIL_ACTION_MAX_ITEMS)),
        );
    } catch (_e) {
        // best effort
    }
};

let flushingPendingActions = false;

/**
 * Replay the persisted mail actions that failed earlier (offline shade action,
 * expired token, …). Serialized against itself; each entry gets a bounded
 * number of attempts and a hard age cap. Called from the fast-path install
 * (app boot / bootstrap remount) and safe to call any time. Never throws.
 *
 * @returns {Promise<number>} How many queued actions completed successfully.
 */
export const flushPendingMailNotificationActions = async () => {
    if (flushingPendingActions || Platform.OS === 'web') {
        return 0;
    }
    flushingPendingActions = true;
    try {
        const raw = await AsyncStorage.getItem(PENDING_MAIL_ACTIONS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        const list = Array.isArray(parsed) ? parsed : [];
        if (!list.length) {
            return 0;
        }

        const keep = [];
        let succeeded = 0;
        for (const entry of list) {
            const age = Date.now() - Number(entry?.at || 0);
            if (!entry?.actionId || !entry?.messageId || age > PENDING_MAIL_ACTION_MAX_AGE_MS) {
                continue; // expired/malformed — drop silently
            }
            try {
                await performMailNotificationAction(
                    String(entry.actionId),
                    String(entry.messageId),
                    String(entry.text || ''),
                    entry.data || {},
                );
                succeeded += 1;
            } catch (_e) {
                const attempts = Number(entry.attempts || 0) + 1;
                if (attempts < PENDING_MAIL_ACTION_MAX_ATTEMPTS) {
                    keep.push({...entry, attempts});
                }
            }
        }

        await AsyncStorage.setItem(PENDING_MAIL_ACTIONS_KEY, JSON.stringify(keep));
        if (succeeded > 0) {
            refreshMailBadgeCount().catch(() => {});
        }

        return succeeded;
    } catch (_e) {
        return 0;
    } finally {
        flushingPendingActions = false;
    }
};

/**
 * Handle a HEADLESS action tapped on a mail notification (inline reply / mark
 * read / archive). Runs the API call with the stored Bearer token — no UI, no
 * navigation. On success the presented notification is dismissed and the app
 * badge refreshed; on failure the action is queued for a bounded retry. Never
 * throws (headless JS must never crash).
 *
 * @param {Object} response expo-notifications response object.
 * @returns {Promise<boolean>} true when the action succeeded immediately.
 */
const handleMailActionResponse = async (response) => {
    try {
        const request = response?.notification?.request;
        const data = request?.content?.data || {};
        const actionId = String(response?.actionIdentifier || '');
        const messageId = String(data.mailMessageId || data.mailmessageid
            || data.messageId || data.messageid || '').trim();
        const text = String(response?.userText || '').trim();

        if (!messageId) {
            return false; // nothing actionable — silent no-op
        }
        if (actionId === MAIL_NOTIFICATION_ACTIONS.reply && !text) {
            return false; // empty inline reply — never send a blank mail
        }

        try {
            await performMailNotificationAction(actionId, messageId, text, data);
        } catch (_e) {
            await queuePendingMailAction({actionId, messageId, text, data});

            return false;
        }

        // Success: retire the banner the action was taken on + refresh the badge.
        try {
            if (request?.identifier && typeof Notifications.dismissNotificationAsync === 'function') {
                await Notifications.dismissNotificationAsync(request.identifier);
            }
        } catch (_e) {
            // dismissal is cosmetic
        }
        refreshMailBadgeCount().catch(() => {});

        return true;
    } catch (_e) {
        return false;
    }
};

let responderSubscription = null;
// In-memory guard against handling the SAME tap twice: on a cold start the
// listener and `getLastNotificationResponseAsync()` can both surface the very
// same response, which would otherwise navigate twice.
let lastHandledResponseId = '';
let lastHandledResponseAt = 0;
const RESPONSE_DEDUPE_MS = 5000;

/**
 * Handle one notification response (a tap). Runs the fast-path BEFORE resolving
 * the target, so the message fetch overlaps the navigation instead of starting
 * after the reader has mounted.
 *
 * @param {Object} response expo-notifications response object.
 * @returns {boolean} true when a navigation target was resolved.
 */
const handleNotificationResponse = (response) => {
    try {
        const request = response?.notification?.request;
        const content = request?.content || {};
        const data = content.data || {};

        const identifier = String(request?.identifier || '');
        if (identifier
            && identifier === lastHandledResponseId
            && Date.now() - lastHandledResponseAt < RESPONSE_DEDUPE_MS) {
            return false;
        }
        lastHandledResponseId = identifier;
        lastHandledResponseAt = Date.now();

        // Headless shade actions (inline reply / mark read / archive) must run
        // WITHOUT foregrounding anything: no launch intent, no navigation —
        // just the API call, fire-and-forget (it queues its own retry).
        const actionId = String(response?.actionIdentifier || '');
        if (isBackgroundMailAction(actionId) && isMailPayload(data)) {
            handleMailActionResponse(response).catch(() => {});

            return true;
        }

        // Tell the BOOT surfaces this launch/foreground is notification-driven,
        // before anything else. On a cold, push-driven start this runs while
        // App.jsx is still loading runtime state (or while the Lottie splash is
        // playing), so SplashScreenWrapper can skip the 1.5-5 s branding
        // animation and the navigator — and therefore the retry loop below —
        // comes up seconds earlier. Marked for EVERY tap, not just mail ones:
        // any tapped notification is an explicit request for a specific screen.
        markNotificationLaunchIntent({source: 'notification-tap'});

        // Workday end-of-day reminder: open the in-app reminder modal (no navigation target).
        if (isWorkdayReminderPayload(data)) {
            requestWorkdayReminder();

            return true;
        }

        // 1. Cache a stub + start the body fetch immediately (never awaited).
        observeMailPushPayload(data);

        // 2. Resolve + navigate durably (queued and replayed while the navigator
        //    is still absent during a cold, push-driven launch).
        const target = resolveNotificationNavigationTarget({
            data,
            notification: {title: content.title || '', body: content.body || ''},
        });
        if (!target?.name) {
            return false;
        }
        const tappedMessage = {
            data,
            notification: {title: content.title || '', body: content.body || ''},
        };
        // Driver pushes may need a mode switch first (or a "not allowed" toast), so
        // they go through the driver router; navigation itself stays durable.
        if (isDriverTarget(target)) {
            openDriverTarget(target, tappedMessage, (name, params) => navigateToNotificationTarget({name, params}, tappedMessage))
                .catch(() => {});

            return true;
        }
        navigateToNotificationTarget(target, tappedMessage);

        return true;
    } catch (_e) {
        // ignore malformed tap payloads
        return false;
    }
};

/**
 * Recover a COLD-START tap on a LOCAL notification. `messaging().getInitialNotification()`
 * (see services/pushNotifications.js) only covers FCM pushes; a tap on a banner
 * raised by expo-notifications — the poll fallback and every re-presented
 * data-only push — is ONLY retrievable through this API.
 *
 * De-duped on the response identifier AND on age, because
 * `getLastNotificationResponseAsync()` keeps returning the same response on
 * later launches and must never hijack a normal app start.
 *
 * @returns {Promise<boolean>} true when a cold-start tap was handled.
 */
export const consumeInitialLocalNotificationResponse = async () => {
    if (Platform.OS === 'web') {
        return false;
    }
    if (!Notifications || typeof Notifications.getLastNotificationResponseAsync !== 'function') {
        return false;
    }

    try {
        const response = await Notifications.getLastNotificationResponseAsync();
        const request = response?.notification?.request;
        if (!request) {
            return false;
        }

        const identifier = String(request.identifier || '');
        const handled = await AsyncStorage.getItem(LAST_HANDLED_RESPONSE_KEY);
        if (identifier && handled === identifier) {
            return false;
        }

        // `date` is ms on Android and seconds on iOS — normalize before comparing.
        const rawDate = Number(response?.notification?.date || 0);
        const at = rawDate > 0 && rawDate < 1e12 ? rawDate * 1000 : rawDate;
        if (at > 0 && Date.now() - at > COLD_START_RESPONSE_MAX_AGE_MS) {
            if (identifier) {
                await AsyncStorage.setItem(LAST_HANDLED_RESPONSE_KEY, identifier).catch(() => {});
            }

            return false;
        }

        if (identifier) {
            await AsyncStorage.setItem(LAST_HANDLED_RESPONSE_KEY, identifier).catch(() => {});
        }

        return handleNotificationResponse(response);
    } catch (_e) {
        return false;
    }
};

let fastPathInstalled = false;

/**
 * Install the whole mail notification fast-path as early as possible — this is
 * called at MODULE SCOPE below, i.e. the instant this module is imported during
 * boot, long before React mounts a home screen. That matters because the tap
 * responder used to be registered only from `PushNotificationsBootstrap` (which
 * mounts after boot + splash + app-mode resolution), so a cold-start tap had to
 * wait out the entire launch before anything mail-related happened.
 *
 * Idempotent: safe to call again from `PushNotificationsBootstrap`.
 *
 * @returns {() => void} Unsubscribe for the tap responder (no-op when already installed).
 */
export const installMailNotificationFastPath = () => {
    if (fastPathInstalled) {
        return () => {};
    }
    fastPathInstalled = true;

    // Actionable 'mail_message' category + any shade actions queued by a
    // previous headless run — both fire-and-forget, neither may block boot.
    registerMailNotificationCategory().catch(() => {});
    flushPendingMailNotificationActions().catch(() => {});

    const unsubscribe = setupMailNotificationResponder();
    // Cold-start tap on a LOCAL banner: not delivered to the listener above, so
    // it has to be pulled explicitly. Fire-and-forget; never blocks boot.
    consumeInitialLocalNotificationResponse().catch(() => {});

    return () => {
        fastPathInstalled = false;
        unsubscribe?.();
    };
};

/**
 * Install the notification-tap responder (idempotent). Routes local + background
 * push taps to the right mail screen via the shared resolver. Returns an
 * unsubscribe function.
 * @returns {() => void}
 */
export const setupMailNotificationResponder = () => {
    if (responderSubscription) {
        return () => {};
    }

    // Guard against builds where the expo-notifications native module isn't
    // present in the registry. Creating the response listener spins up an Expo
    // Kotlin event emitter against that module; if it's missing the native side
    // throws "Cannot create an event emitter for the module that isn't present
    // in the module registry" (Sentry HAPPY-APP-H). Wrap so a missing module
    // degrades to a no-op responder instead of crashing.
    if (!Notifications || typeof Notifications.addNotificationResponseReceivedListener !== 'function') {
        return () => {};
    }

    try {
        responderSubscription = Notifications.addNotificationResponseReceivedListener((response) => {
            handleNotificationResponse(response);
        });
    } catch (error) {
        console.warn('[mailNotifications] Notification responder unavailable:', error?.message || error);
        responderSubscription = null;
        return () => {};
    }

    return () => {
        try {
            responderSubscription?.remove?.();
        } catch (_e) {
            // ignore teardown errors
        }
        responderSubscription = null;
    };
};

// Install the tap responder + cold-start recovery at MODULE LOAD (this module is
// imported eagerly during boot), so a mail notification tap is honoured even
// when it happens while the runtime-state load / splash still owns the screen.
// Guarded: a missing native module degrades to a no-op, never a boot crash.
if (Platform.OS !== 'web') {
    // One tick of deferral so nothing native is touched DURING entry-module
    // evaluation (the boot-abort hazard documented in index.js), while still
    // running long before the navigator or any home screen exists.
    globalThis.setTimeout(() => {
        try {
            installMailNotificationFastPath();
        } catch (_e) {
            // never let notification wiring abort boot
        }
    }, 0);
}
